import type { LoadoutSlotKey } from "../../hooks/use-current-loadout.ts";
import type { UseItemsResult } from "../../hooks/use-items.ts";
import {
	armorIcon,
	weaponIcon,
} from "../dossier/loadout/loadout-slot-match.ts";

const STARTER_SOURCE_NAME = "Starter Equipment";

export function slugify(name: string): string {
	return name
		.toLowerCase()
		.replace(/[^a-z0-9]+/g, "-")
		.replace(/(^-|-$)/g, "");
}

export function sourceSlug(warbondSource: string | null | undefined): string {
	return slugify(warbondSource || STARTER_SOURCE_NAME);
}

export interface PickerItem {
	slug: string;
	name: string;
	iconUrl: string | null;
	sourceSlug: string;
	warbondSource: string;
}

export function getValidItems(
	slotKey: LoadoutSlotKey,
	items: UseItemsResult,
): PickerItem[] {
	if (slotKey === "primary") {
		return items.weapons
			.filter((w) => w.category === "Primary")
			.map((w) => ({
				slug: slugify(w.name),
				name: w.name,
				iconUrl: weaponIcon(w, "sm"),
				sourceSlug: sourceSlug(w.warbondSource),
				warbondSource: w.warbondSource || STARTER_SOURCE_NAME,
			}));
	}
	if (slotKey === "secondary") {
		return items.weapons
			.filter((w) => w.category === "Secondary")
			.map((w) => ({
				slug: slugify(w.name),
				name: w.name,
				iconUrl: weaponIcon(w, "sm"),
				sourceSlug: sourceSlug(w.warbondSource),
				warbondSource: w.warbondSource || STARTER_SOURCE_NAME,
			}));
	}
	if (slotKey === "grenade") {
		return items.weapons
			.filter((w) => w.category === "Throwable")
			.map((w) => ({
				slug: slugify(w.name),
				name: w.name,
				iconUrl: weaponIcon(w, "sm"),
				sourceSlug: sourceSlug(w.warbondSource),
				warbondSource: w.warbondSource || STARTER_SOURCE_NAME,
			}));
	}
	if (slotKey === "stratagems") {
		return items.stratagems
			.filter((s) => s.category !== "Mission")
			.map((s) => ({
				slug: slugify(s.name),
				name: s.name,
				iconUrl: s.icon ? `/item-assets/${s.icon}` : null,
				sourceSlug: sourceSlug(s.warbondSource),
				warbondSource: s.warbondSource || STARTER_SOURCE_NAME,
			}));
	}
	if (slotKey === "booster") {
		return items.boosters.map((b) => ({
			slug: slugify(b.name),
			name: b.name,
			iconUrl: b.icon ? `/item-assets/${b.icon}` : null,
			sourceSlug: sourceSlug(b.warbondSource),
			warbondSource: b.warbondSource || STARTER_SOURCE_NAME,
		}));
	}
	if (slotKey === "armor") {
		const out: PickerItem[] = [];
		for (const p of items.passives) {
			for (const a of p.armors) {
				out.push({
					slug: slugify(a.name),
					name: a.name,
					iconUrl: armorIcon(a, "sm"),
					sourceSlug: sourceSlug(a.warbondSource),
					warbondSource: a.warbondSource || STARTER_SOURCE_NAME,
				});
			}
		}
		return out;
	}
	return [];
}
